import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { useEffect } from 'react';
import { Helmet } from 'react-helmet-async';
/* Per-page SEO: title, description, canonical and social cards in one place.
   Path is the canonical route, e.g. "/services/fractional-caio". */
const BASE = 'https://www.ncrglobalservices.com';
/* BreadcrumbList structured data derived from the route, e.g.
   /services/managed-ai -> Home > Services > Managed Ai. */
const breadcrumbJsonLd = (path, pageName) => {
    const segments = path.split('/').filter(Boolean);
    const pretty = (s) => s.split('-').map(w => w.charAt(0).toUpperCase() + w.slice(1)).join(' ');
    const items = [
        { '@type': 'ListItem', position: 1, name: 'Home', item: `${BASE}/` },
        ...segments.map((seg, i) => ({
            '@type': 'ListItem',
            position: i + 2,
            name: i === segments.length - 1 ? pageName : pretty(seg),
            item: `${BASE}/${segments.slice(0, i + 1).join('/')}`
        }))
    ];
    return { '@context': 'https://schema.org', '@type': 'BreadcrumbList', itemListElement: items };
};
const Seo = ({ title, description, path, keywords, jsonLd }) => {
    /* The static fallback tags in index.html serve crawlers that skip JavaScript.
       Once React runs, Helmet owns these tags, so the static ones must go or a
       page would carry two canonicals pointing at different URLs. */
    useEffect(() => {
        document
            .querySelectorAll('head link[rel="canonical"]:not([data-rh]), head meta[name="description"]:not([data-rh]), head meta[property^="og:"]:not([data-rh]), head meta[name^="twitter:"]:not([data-rh])')
            .forEach(el => el.remove());
    }, []);
    return (_jsxs(Helmet, { defer: false, children: [_jsx("title", { children: title }), _jsx("meta", { name: "description", content: description }), keywords && keywords.length > 0 && _jsx("meta", { name: "keywords", content: keywords.join(', ') }), _jsx("meta", { name: "robots", content: "index, follow, max-image-preview:large, max-snippet:-1" }), _jsx("meta", { property: "og:locale", content: "en_US" }), _jsx("link", { rel: "canonical", href: `${BASE}${path}` }), _jsx("meta", { property: "og:type", content: "website" }), _jsx("meta", { property: "og:site_name", content: "NCR Global Services" }), _jsx("meta", { property: "og:title", content: title }), _jsx("meta", { property: "og:description", content: description }), _jsx("meta", { property: "og:url", content: `${BASE}${path}` }), _jsx("meta", { property: "og:image", content: `${BASE}/ncr-logo.webp` }), _jsx("meta", { name: "twitter:card", content: "summary" }), _jsx("meta", { name: "twitter:title", content: title }), _jsx("meta", { name: "twitter:description", content: description }), jsonLd && _jsx("script", { type: "application/ld+json", children: JSON.stringify(jsonLd) }), path !== '/' && (_jsx("script", { type: "application/ld+json", children: JSON.stringify(breadcrumbJsonLd(path, title.split('|')[0].trim())) }))] }));
};
export default Seo;
/* schema.org Service node for individual service pages, linked to the site Organization. */
export const serviceJsonLd = (name, description, path) => ({
    '@context': 'https://schema.org',
    '@type': 'Service',
    name,
    description,
    url: `${BASE}${path}`,
    serviceType: name,
    provider: { '@id': 'https://www.ncrglobalservices.com/#org' },
    areaServed: ['AE', 'IN', 'US', 'EU', 'AU']
});
